/**
 * Development status panel: is the app running, is the API up, is the database
 * reachable. Scaffolding, replaced by the scan flow.
 */

import { useEffect, useState } from 'react';

import { fetchHealth, type HealthOutcome } from '../data/health-api.ts';
import { StatusChip } from './StatusChip.tsx';
import { StatusRow } from './StatusRow.tsx';

export function SystemStatus() {
  const outcome = useHealth();

  return (
    <dl className="flex flex-col gap-3">
      <StatusRow term="Web app">
        <StatusChip tone="good" label="Running on port 5174" />
      </StatusRow>
      <StatusRow term="API">
        <ApiStatus outcome={outcome} />
      </StatusRow>
      <StatusRow term="Database">
        <DatabaseStatus outcome={outcome} />
      </StatusRow>
    </dl>
  );
}

function useHealth(): HealthOutcome | undefined {
  const [outcome, setOutcome] = useState<HealthOutcome | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    void fetchHealth().then((result) => {
      if (!cancelled) {
        setOutcome(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return outcome;
}

function ApiStatus({ outcome }: { readonly outcome: HealthOutcome | undefined }) {
  if (outcome === undefined) {
    return <StatusChip tone="muted" label="Checking..." />;
  }

  if (outcome.kind === 'unreachable') {
    return <StatusChip tone="critical" label="Not running - start it with npm run dev:server" />;
  }

  return <StatusChip tone="good" label={`Running, version ${outcome.report.version}`} />;
}

function DatabaseStatus({ outcome }: { readonly outcome: HealthOutcome | undefined }) {
  if (outcome === undefined) {
    return <StatusChip tone="muted" label="Checking..." />;
  }

  if (outcome.kind === 'unreachable' || outcome.report.database === 'down') {
    return <StatusChip tone="warning" label="Not reachable - PostgreSQL not installed yet" />;
  }

  return <StatusChip tone="good" label="Connected" />;
}
